import { getCurrencyCode, Language } from '@/utilities/language';

interface CurrencyDetails {
  amount_decimal: number;
  amount_int: number;
  currency: string;
  precision: number;
}

export const CurrencyCode = {
  CZK: 'czk',
  EUR: 'eur',
} as const;

export type CurrencyCode = (typeof CurrencyCode)[keyof typeof CurrencyCode];

export type CurrencyCollection = {
  [key in CurrencyCode]: CurrencyDetails;
};

export class Price {
  private data: CurrencyCollection;

  constructor(data: CurrencyCollection) {
    this.data = data;
  }

  /**
   * Retrieves currency details for a given code, which can be a Language or a CurrencyCode.
   * @param code The Language or CurrencyCode to get details for.
   * @returns The CurrencyDetails object or undefined if not found.
   */
  public getCurrencyDetails(
    code: CurrencyCode | Language,
  ): CurrencyDetails | undefined {
    // Determine the final CurrencyCode based on the input type
    let currencyCode: CurrencyCode;
    if (Object.values(Language).includes(code as Language)) {
      currencyCode = getCurrencyCode(code as Language);
    } else {
      currencyCode = code as CurrencyCode;
    }

    return this.data[currencyCode];
  }
}

/**
 *
 * @param price
 * @returns
 */
export function formatPrice(price: number) {
  // Separate the integer and decimal parts of the number.
  const integerPart = Math.floor(price);
  const decimalPartValue = price % 1;

  // Use a regular expression to insert a space as a thousands separator.
  const formattedIntegerPart = integerPart
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (decimalPartValue !== 0) {
    // Check if there is a decimal part to format.
    // Get the first two decimal digits by multiplying by 100 and taking the floor.
    const decimalString = Math.floor(decimalPartValue * 100)
      .toString()
      .padStart(2, '0'); // Pad with a leading zero if necessary (e.g., 0.05).
    return `${formattedIntegerPart}.${decimalString}`;
  }

  // Return only the integer part if there are no decimals.
  return formattedIntegerPart;
}
